import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, Building, MapPin, Briefcase, Loader2, Linkedin, Globe, FileText, Landmark } from 'lucide-react';
import { useKB } from './KBContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorFromName = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `hsl(${h}, 30%, 55%)`;
};

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const MemberAvatar = ({ member, size = 64 }) => {
  const [errored, setErrored] = useState(false);
  const photo = member.photo_url || member.photo_url_small;
  if (photo && !errored) {
    return (
      <img
        src={photo}
        alt={member.full_name}
        className="kb-member-avatar"
        style={{ width: size, height: size }}
        onError={() => setErrored(true)}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className="kb-member-avatar kb-member-avatar-initials"
      style={{
        width: size,
        height: size,
        background: colorFromName(member.full_name || ''),
        fontSize: size * 0.32,
      }}
      aria-label={member.full_name}
    >
      {initials(member.full_name)}
    </div>
  );
};

/**
 * MemberSheet — slide-in panel with full contact info.
 * Used by MembersPanel (directory click) AND DocumentDetail (author chip click).
 *
 * Accepts either a `member` (basic record from list) which it then hydrates
 * via /api/kb/members/{id}, or a `memberId` to fetch directly.
 */
const MemberSheet = ({ member, memberId, onClose, onCompanyClick }) => {
  const { password } = useKB();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAllExpertise, setShowAllExpertise] = useState(false);

  const idToFetch = member?.member_id || memberId;

  useEffect(() => {
    if (!idToFetch) return;
    let cancelled = false;
    setLoading(true);
    setShowAllExpertise(false);
    const fd = new FormData();
    fd.append('password', password);
    fetch(`${API_URL}/api/kb/members/${idToFetch}`, { method: 'POST', body: fd })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setDetail(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [idToFetch, password]);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const view = detail || member || {};

  // Full expertise = member-curated tags first, then AI-inferred topics not
  // already listed (deduped, case-insensitive). Some members have 50+ topics,
  // so we collapse to a preview and reveal the rest with "See all expertise".
  const EXPERTISE_PREVIEW = 8;
  const expertise = (() => {
    const out = [];
    const seen = new Set();
    [...(Array.isArray(view.tags) ? view.tags : []),
     ...(Array.isArray(view.expertise_topics) ? view.expertise_topics : [])]
      .forEach((t) => {
        const key = String(t || '').trim().toLowerCase();
        if (key && !seen.has(key)) { seen.add(key); out.push(t); }
      });
    return out;
  })();
  const visibleExpertise = showAllExpertise ? expertise : expertise.slice(0, EXPERTISE_PREVIEW);
  const hiddenExpertiseCount = expertise.length - EXPERTISE_PREVIEW;

  return (
    <AnimatePresence>
      <motion.div
        key="member-sheet-backdrop"
        className="kb-member-sheet-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        data-testid="kb-member-sheet-backdrop"
      />
      <motion.aside
        key="member-sheet-panel"
        className="kb-member-sheet"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        data-testid="kb-member-sheet"
      >
        <button
          className="kb-member-sheet-close"
          onClick={onClose}
          aria-label="Close member profile"
          data-testid="kb-member-sheet-close"
        >
          <X size={18} />
        </button>

        {loading && !detail ? (
          <div className="kb-members-loading"><Loader2 className="spin" size={20} /> Loading…</div>
        ) : (
          <>
            <div className="kb-member-sheet-hero">
              <MemberAvatar member={view} size={120} />
              <h2 className="kb-member-sheet-name">{view.full_name}</h2>
              {view.is_staff && (
                <span data-testid="kb-member-sheet-staff-badge" style={{
                  marginTop: 6, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                  background: 'var(--kb-accent, #2563EB)', color: '#fff',
                }}>IPMI Staff</span>
              )}
              {view.position_title && (
                <div className="kb-member-sheet-position">{view.position_title}</div>
              )}
              {(view.authored_doc_count > 0) && (
                <div data-testid="kb-member-sheet-authored" style={{
                  marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)',
                }}>
                  <FileText size={12} /> {view.authored_doc_count} authored {view.authored_doc_count === 1 ? 'document' : 'documents'}
                </div>
              )}
            </div>

            {view.bio && (
              <div className="kb-member-sheet-bio" data-testid="kb-member-sheet-bio" style={{
                padding: '0 24px 4px', fontSize: 14, lineHeight: 1.55, color: 'var(--kb-fg)',
              }}>
                {view.bio}
              </div>
            )}

            {Array.isArray(detail?.governance) && detail.governance.length > 0 && (
              <div data-testid="kb-member-sheet-governance" style={{ padding: '16px 24px 0' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 10,
                }}>
                  <Landmark size={12} /> IPMI Roles
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {detail.governance.map((r, i) => (
                    <div key={i} data-testid="kb-member-sheet-gov-role" style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                      borderRadius: 10, border: '1px solid rgba(37,99,235,0.28)',
                      background: 'rgba(37,99,235,0.06)',
                    }}>
                      <span style={{
                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                        background: 'var(--kb-accent, #2563EB)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Landmark size={15} />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--kb-fg)' }}>{r.group_name}</div>
                        {r.role && (
                          <div style={{ fontSize: 12, color: 'var(--kb-accent, #2563EB)', fontWeight: 500 }}>{r.role}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="kb-member-sheet-body">
              {view.company_name && (
                <div className="kb-member-sheet-row">
                  <Building size={14} />
                  <div style={{ flex: 1 }}>
                    <div className="kb-member-sheet-row-label">Company</div>
                    <div className="kb-member-sheet-row-value">{view.company_name}</div>
                    {onCompanyClick && (
                      <button
                        type="button"
                        data-testid="kb-member-sheet-view-team"
                        onClick={() => onCompanyClick(view.company_name)}
                        style={{
                          marginTop: 8,
                          background: 'rgba(91,154,255,0.10)',
                          border: '1px solid rgba(91,154,255,0.32)',
                          color: '#5B9AFF',
                          padding: '6px 12px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          letterSpacing: '0.02em',
                        }}>
                        View team at {view.company_name} →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {(view.city || view.country) && (
                <div className="kb-member-sheet-row">
                  <MapPin size={14} />
                  <div>
                    <div className="kb-member-sheet-row-label">Location</div>
                    <div className="kb-member-sheet-row-value">
                      {[view.city, view.country].filter(Boolean).join(', ')}
                    </div>
                  </div>
                </div>
              )}

              {detail?.email && (
                <a
                  className="kb-member-sheet-row kb-member-sheet-row-link"
                  href={`mailto:${detail.email}`}
                  data-testid="kb-member-sheet-email"
                >
                  <Mail size={14} />
                  <div>
                    <div className="kb-member-sheet-row-label">Email</div>
                    <div className="kb-member-sheet-row-value">{detail.email}</div>
                  </div>
                </a>
              )}

              {detail?.phone && (
                <a
                  className="kb-member-sheet-row kb-member-sheet-row-link"
                  href={`tel:${detail.phone.replace(/[^+0-9]/g, '')}`}
                  data-testid="kb-member-sheet-phone"
                >
                  <Phone size={14} />
                  <div>
                    <div className="kb-member-sheet-row-label">Phone</div>
                    <div className="kb-member-sheet-row-value">{detail.phone}</div>
                  </div>
                </a>
              )}

              {!loading && detail && !detail.email && !detail.phone && (
                <div className="kb-member-sheet-no-contact">
                  Contact details not available for this member.
                </div>
              )}

              {detail?.links?.linkedin && (
                <a
                  className="kb-member-sheet-row kb-member-sheet-row-link"
                  href={detail.links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kb-member-sheet-linkedin"
                >
                  <Linkedin size={14} />
                  <div>
                    <div className="kb-member-sheet-row-label">LinkedIn</div>
                    <div className="kb-member-sheet-row-value">View profile</div>
                  </div>
                </a>
              )}

              {detail?.links?.website && (
                <a
                  className="kb-member-sheet-row kb-member-sheet-row-link"
                  href={detail.links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kb-member-sheet-website"
                >
                  <Globe size={14} />
                  <div>
                    <div className="kb-member-sheet-row-label">Website</div>
                    <div className="kb-member-sheet-row-value">{detail.links.website.replace(/^https?:\/\//, '')}</div>
                  </div>
                </a>
              )}
            </div>

            {expertise.length > 0 && (
              <div data-testid="kb-member-sheet-expertise" style={{ padding: '4px 24px 0' }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 10,
                }}>
                  Areas of Expertise
                </div>
                <div data-testid="kb-member-sheet-tags" style={{
                  display: 'flex', flexWrap: 'wrap', gap: 8,
                }}>
                  {visibleExpertise.map((t, i) => (
                    <span key={i} style={{
                      padding: '5px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
                      background: 'rgba(37,99,235,0.10)', border: '1px solid rgba(37,99,235,0.3)',
                      color: 'var(--kb-accent, #2563EB)',
                    }}>{t}</span>
                  ))}
                </div>
                {expertise.length > EXPERTISE_PREVIEW && (
                  <button
                    type="button"
                    data-testid="kb-member-sheet-expertise-toggle"
                    onClick={() => setShowAllExpertise(v => !v)}
                    style={{
                      marginTop: 12, background: 'none', border: 'none', padding: 0,
                      fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      color: 'var(--kb-accent, #2563EB)',
                    }}
                  >
                    {showAllExpertise ? 'Show less' : `See all expertise (${hiddenExpertiseCount} more)`}
                  </button>
                )}
              </div>
            )}

            <div className="kb-member-sheet-footer">
              <em>Synced from Glue Up · Contact only shown to authenticated viewers</em>
            </div>
          </>
        )}
      </motion.aside>
    </AnimatePresence>
  );
};

export default MemberSheet;
